import axios from 'axios';
import { clearStoredSession, getStoredSession, storeSession } from './authStorage';

// Default: local tokenizer API (see backend launchSettings). Override with REACT_APP_CORE_TOKENIZER_API_URL for other environments.
export const API_BASE_URL =
  process.env.REACT_APP_CORE_TOKENIZER_API_URL || 'https://api-develop.tearsoftraders.com/tokenizer';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000
});

let refreshPromise = null;

const isAuthRoute = (url = '') =>
  url.includes('/api/auth/login') ||
  url.includes('/api/auth/register') ||
  url.includes('/api/auth/refresh-token');

const refreshAccessToken = async () => {
  const session = getStoredSession();

  if (!session?.refreshToken || !session?.userId) {
    clearStoredSession();
    throw new Error('No refresh session available.');
  }

  const response = await axios.post(
    `${API_BASE_URL}/api/auth/refresh-token`,
    {
      userId: session.userId,
      refreshToken: session.refreshToken
    },
    {
      timeout: 30000
    }
  );

  const refreshedSession = {
    ...session,
    ...response.data
  };

  storeSession(refreshedSession);
  return refreshedSession;
};

api.interceptors.request.use((config) => {
  const session = getStoredSession();

  if (session?.accessToken) {
    config.headers = {
      ...(config.headers || {}),
      Authorization: `Bearer ${session.accessToken}`
    };
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const statusCode = error.response?.status;

    if (
      !originalRequest ||
      originalRequest._retry ||
      statusCode !== 401 ||
      isAuthRoute(originalRequest.url)
    ) {
      throw error;
    }

    try {
      originalRequest._retry = true;

      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }

      const refreshedSession = await refreshPromise;
      originalRequest.headers = {
        ...(originalRequest.headers || {}),
        Authorization: `Bearer ${refreshedSession.accessToken}`
      };

      return api(originalRequest);
    } catch (refreshError) {
      clearStoredSession();
      throw refreshError;
    }
  }
);

export default api;
